/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { getEnvVarOptional, COSMOS_SITE_BASE_PATH } from "@cosmos/common-utils";

import { getLocalizedPath, isQueryParamLocaleMode } from "../utils/locale-path.utils";

import { buildLocalizedUrl } from "./url.utils";

import type { LocalePrefixMode } from "../types/locale.types";

export interface AlternatesProps {
	canonical: string;
	languages: { [key: string]: string };
}

export const getAlternates = (
	locale: string,
	slug: string,
	locales?: string[],
	defaultLocale?: string,
	localePrefix?: LocalePrefixMode,
): AlternatesProps => {
	const { fullUrl } = buildLocalizedUrl(locale, slug, defaultLocale);
	const languages: { [key: string]: string } = {};

	if (locales?.length) {
		const normalizedSlug = slug === "/home" ? "" : slug;
		// Optional access — metadata should degrade gracefully without EPIC_SERVER_URL
		const baseUrl = `${getEnvVarOptional("EPIC_SERVER_URL") ?? ""}${COSMOS_SITE_BASE_PATH()}`;
		const effectiveDefault = defaultLocale || locale;

		if (isQueryParamLocaleMode() && baseUrl) {
			// Query-param mode: use buildLocalizedUrl (handles ?lang=)
			const cleanUrl = `${baseUrl}${normalizedSlug || "/"}`;
			for (const loc of locales) {
				if (loc === effectiveDefault) continue;
				languages[loc] = buildLocalizedUrl(loc, slug, effectiveDefault).fullUrl;
			}
			// Default locale uses clean URL (no ?lang= param)
			languages[effectiveDefault] = cleanUrl;
			languages["x-default"] = cleanUrl;
		} else {
			// Path-based mode: use getLocalizedPath (handles "always" + "as-needed")
			// Returns relative paths — Next.js metadataBase resolves to full URLs
			const prefix = localePrefix || "always";
			for (const loc of locales) {
				languages[loc] = getLocalizedPath(normalizedSlug || "/", loc, effectiveDefault, prefix);
			}
			// x-default: default locale URL
			languages["x-default"] = getLocalizedPath(normalizedSlug || "/", effectiveDefault, effectiveDefault, prefix);
		}
	}

	return { canonical: fullUrl, languages };
};
